// 定义返回数据的类型接口
interface MemeTokenInfo {
  success: boolean;
  data?: {
    basic: {
      address: string;
      name: string;
      shortName: string;
      symbol: string;
      logo: string;
      description: string;
      webUrl?: string;
    };
    pricing: {
      price: string;
      maxPrice: string;
      marketCap: string;
      increase: string;
      dayIncrease: string;
      trading: string;
      dayTrading: string;
      raisedAmount: string;
      progress: string;
      liquidity: string;
    };
    pairing: {
      valueToken: string; // 配对的价值代币 (BNB, USD1等)
      pairAddress?: string;
      tradeUrl?: string;
    };
    launchStatus: {
      isLaunched: boolean; // 是否已发射
      dexType: string; // PANCAKE_SWAP
      version?: string; // V2 或 V3
      launchTime?: number;
    };
    metadata: {
      totalAmount: string;
      saleAmount: string;
      networkCode: string;
      createDate: string;
      userId: number;
      userAddress: string;
    };
  };
  error?: string;
}

export async function getFourMemeTokenInfo(address: string): Promise<MemeTokenInfo> {
  let url = "https://four.meme/meme-api/v1/private/token/get/v2?address=" + address;

  try {
    const response = await fetch(url);
    const rawData = await response.json();
    
    // 检查API响应状态
    if (rawData.code !== 0) {
      return {
        success: false,
        error: rawData.msg || "API调用失败"
      };
    }

    // 检查是否有有效的代币数据
    if (!rawData.data || typeof rawData.data !== 'object') {
      return {
        success: false,
        error: "无效的代币地址或代币不存在"
      };
    }

    const tokenData = rawData.data;
    
    // 验证必要的字段是否存在
    if (!tokenData.address || !tokenData.name || !tokenData.tokenPrice) {
      return {
        success: false,
        error: "代币数据不完整"
      };
    }
    
    // 判断是否已发射到pancakeswap
    const isLaunched = tokenData.status === "TRADE";
    
    // 获取pancakeswap版本信息
    let pancakeVersion: string | undefined;
    if (isLaunched && tokenData.dexPair) {
      pancakeVersion = tokenData.dexPair.pancakeVersion === 2 ? "V2" : 
                      tokenData.dexPair.pancakeVersion === 3 ? "V3" : 
                      tokenData.version || "未知版本";
    }

    // 构造优化后的返回数据
    const optimizedData: MemeTokenInfo = {
      success: true,
      data: {
        basic: {
          address: tokenData.address,
          name: tokenData.name,
          shortName: tokenData.shortName,
          symbol: tokenData.symbol, // 这是配对的价值代币符号
          logo: tokenData.image, // meme代币的logo
          description: tokenData.descr,
          webUrl: tokenData.webUrl
        },
        pricing: {
          price: tokenData.tokenPrice.price,
          maxPrice: tokenData.tokenPrice.maxPrice,
          marketCap: tokenData.tokenPrice.marketCap,
          increase: tokenData.tokenPrice.increase,
          dayIncrease: tokenData.tokenPrice.dayIncrease,
          trading: tokenData.tokenPrice.trading,
          dayTrading: tokenData.tokenPrice.dayTrading,
          raisedAmount: tokenData.tokenPrice.raisedAmount,
          progress: tokenData.tokenPrice.progress,
          liquidity: tokenData.tokenPrice.liquidity
        },
        pairing: {
          valueToken: tokenData.symbol, // 配对的价值代币 (BNB, USD1等)
          pairAddress: tokenData.dexPair?.pairAddress,
          tradeUrl: tokenData.tradeUrl
        },
        launchStatus: {
          isLaunched: isLaunched,
          dexType: tokenData.dexType,
          version: pancakeVersion,
          launchTime: isLaunched ? tokenData.launchTime : undefined
        },
        metadata: {
          totalAmount: tokenData.totalAmount,
          saleAmount: tokenData.saleAmount,
          networkCode: tokenData.networkCode,
          createDate: tokenData.createDate,
          userId: tokenData.userId,
          userAddress: tokenData.userAddress
        }
      }
    };

    return optimizedData;
    
  } catch (error) {
    console.error("获取meme代币信息失败:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "网络请求失败"
    };
  }
}

// 辅助函数：检查代币是否已发射
export function isTokenLaunched(tokenInfo: MemeTokenInfo): boolean {
  return tokenInfo.data?.launchStatus.isLaunched || false;
}

// 辅助函数：获取配对代币信息
export function getPairingInfo(tokenInfo: MemeTokenInfo) {
  if (!tokenInfo.data) return null;
  
  return {
    valueToken: tokenInfo.data.pairing.valueToken,
    pairAddress: tokenInfo.data.pairing.pairAddress,
    tradeUrl: tokenInfo.data.pairing.tradeUrl,
    dexVersion: tokenInfo.data.launchStatus.version
  };
}

// 辅助函数：获取代币logo
export function getTokenLogo(tokenInfo: MemeTokenInfo): string | null {
  return tokenInfo.data?.basic.logo || null;
}

export function getTokenShortName(tokenInfo: MemeTokenInfo): string | null {
  return tokenInfo.data?.basic.shortName || null;
}